import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

interface StatusItem {
    statusID: number;
    name: string;
}

interface TicketFilterDialogProps {
    open: boolean;
    onClose: () => void;
    onApplyFilters: (filters: FilterState) => void;
    onResetFilters: () => void;
    activeFilters: FilterState;
    statusList: StatusItem[];
}

export interface FilterState {
    status: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
}

export const TicketFilterDialog: React.FC<TicketFilterDialogProps> = ({
    open,
    onClose,
    onApplyFilters,
    onResetFilters,
    activeFilters,
    statusList,
}) => {
    const [filters, setFilters] = useState<FilterState>(activeFilters);

    // Update local state when activeFilters prop changes
    useEffect(() => {
        setFilters(activeFilters);
    }, [activeFilters, open]);

    const handleApply = useCallback(() => {
        onApplyFilters(filters);
    }, [filters, onApplyFilters]);

    const handleReset = useCallback(() => {
        const resetFilters: FilterState = {
            status: "",
            fromDate: null,
            toDate: null,
        };
        setFilters(resetFilters);
        onResetFilters();
    }, [onResetFilters]);

    const handleClose = useCallback(() => {
        setFilters(activeFilters); // Reset to active filters on close without applying
        onClose();
    }, [activeFilters, onClose]);

    const getActiveFilterCount = () => {
        let count = 0;
        if (filters.status) count++;
        if (filters.fromDate) count++;
        if (filters.toDate) count++;
        return count;
    };

    const isApplyDisabled = () => {
        const isSameStatus = filters.status === activeFilters.status;
        const isSameFromDate =
            (filters.fromDate === null && activeFilters.fromDate === null) ||
            (filters.fromDate?.isSame(activeFilters.fromDate, 'day'));
        const isSameToDate =
            (filters.toDate === null && activeFilters.toDate === null) ||
            (filters.toDate?.isSame(activeFilters.toDate, 'day'));

        return isSameStatus && isSameFromDate && isSameToDate;
    };

    // Prepare status options for radio group
    const statusOptions = React.useMemo(() => [
        { id: "", label: "All" },
        ...statusList.map(status => ({
            id: status.statusID.toString(),
            label: status.name
        }))
    ], [statusList]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #E5E7EB",
                    pb: 2,
                }}
            >
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#1F2937" }}>
                    Filter Tickets
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2.5 }}>
                {/* Status Filter */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#374151",
                            mb: 1.5,
                        }}
                    >
                        Status
                    </Typography>
                    <FormControl fullWidth>
                        <RadioGroup
                            value={filters.status}
                            onChange={(e) =>
                                setFilters({ ...filters, status: e.target.value })
                            }
                        >
                            {statusOptions.map((status) => (
                                <FormControlLabel
                                    key={status.id}
                                    value={status.id}
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#D1D5DB",
                                                "&.Mui-checked": { color: "#3A1C73" },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: 14, color: "#374151" }}>
                                            {status.label}
                                        </Typography>
                                    }
                                    sx={{
                                        mx: 0,
                                        mb: 0.5,
                                        "&:hover": { bgcolor: "#F9FAFB" },
                                        borderRadius: "6px",
                                        px: 1,
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Date Range Filter */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#374151",
                            mb: 1.5,
                        }}
                    >
                        Date Range
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <DatePicker
                                label="From Date"
                                value={filters.fromDate}
                                onChange={(newValue) =>
                                    setFilters({ ...filters, fromDate: newValue })
                                }
                                maxDate={filters.toDate || undefined}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        sx: {
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        },
                                    },
                                }}
                            />
                            <DatePicker
                                label="To Date"
                                value={filters.toDate}
                                onChange={(newValue) =>
                                    setFilters({ ...filters, toDate: newValue })
                                }
                                minDate={filters.fromDate || undefined}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        sx: {
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </LocalizationProvider>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 2.5,
                    borderTop: "1px solid #E5E7EB",
                    display: "flex",
                    gap: 1.5,
                }}
            >
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleReset}
                    sx={{
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        textTransform: "none",
                        borderRadius: "8px",
                        py: 1,
                        fontWeight: 500,
                        "&:hover": {
                            borderColor: "#9CA3AF",
                            bgcolor: "#F9FAFB",
                        },
                    }}
                >
                    Reset All
                </Button>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApply}
                    disabled={isApplyDisabled()}
                    sx={{
                        bgcolor: isApplyDisabled() ? "#E5E7EB" : "#3A1C73",
                        color: isApplyDisabled() ? "#6B7280" : "#fff",
                        textTransform: "none",
                        borderRadius: "8px",
                        py: 1,
                        fontWeight: 500,
                        boxShadow: "none",
                        "&:hover": {
                            bgcolor: isApplyDisabled() ? "#E5E7EB" : "#2E1556",
                            boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                            bgcolor: "#E5E7EB",
                            color: "#6B7280",
                        },
                    }}
                >
                    Apply Filters
                    {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};